import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { StaticPageAdapter, SubscriptionAdapter, TestimonialAdapter } from '@app/adapters/_index';
import {
  CardTypeCallToActions,
  EloquaFormType,
  Product,
  ProductShortName,
  RouteTypes,
  SubscriptionType,
  TestimonialViewTypes,
} from '@app/enums/_index';
import {
  HasAnalytics,
  HasPageTitleAndMetaData,
  StaticPageInterface,
  TestimonialInterface,
} from '@app/interfaces/_index';
import {
  LMSSubscription,
  StaticPage,
  SubscriptionUIConfigurationItem,
  Testimonial,
  User,
} from '@app/models/_index';
import {
  AnnouncementService,
  GoogleAnalyticsService,
  SubscriptionService,
  UserService,
  WordpressSubscriptionsService,
  WordpressTestimonialsService,
} from '@app/services/_index';
import {
  getSubscriptionSlugByProductShortName,
  lockBackgroundScrolling,
  logGoogleAnalytics,
  scrollToPosition,
  unlockBackgroundScrolling,
} from '@app/utilities/_index';

import { SubscriptionUIConfigurationItemFactory } from '@app/factories/subscription-ui-configuration-item.factory';
import { environment } from '@environments/environment';
import { Subscription } from 'rxjs';

declare var loadEloquaForm: any;

@Component({
  selector: 'app-subscription-detail',
  templateUrl: './subscription-detail.component.html',
  styleUrls: ['./subscription-detail.component.scss'],
  standalone: false,
})
export class SubscriptionDetailComponent
  implements OnInit, OnDestroy, HasPageTitleAndMetaData, HasAnalytics
{
  subs: Subscription[] = [];
  isLoading: boolean;
  environment: any;
  announcementContent: StaticPage;
  currentUser: User;
  name: string; // Current selected subscription slug name
  selectedSubscription: LMSSubscription;
  selectedSubscriptionTestimonial: Testimonial[] = [];
  selectedSubscriptionUIConfiguration: SubscriptionUIConfigurationItem;
  showHeroButton: boolean;
  testimonials: Testimonial[] = []; // Contains CMS related Testimonials related to selected subscription
  showContactSales = environment.beyond.featureFlags.showContactSales;
  contactSalesForm = environment.beyond.featureFlags.contactSalesForm;

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private titleService: Title,
    private metaService: Meta,
    private testimonialAdapter: TestimonialAdapter,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
    private wpTestimonialService: WordpressTestimonialsService,
    private wpSubscriptionService: WordpressSubscriptionsService,
    private subsAdapter: SubscriptionAdapter,
    private staticPageAdapter: StaticPageAdapter,
    private announcementService: AnnouncementService,
    private subscriptionUIItemFactory: SubscriptionUIConfigurationItemFactory
  ) {}

  /**
   * OnInit load selected subscription, title and metadata, and related content
   */
  async ngOnInit(): Promise<void> {
    this.environment = environment;
    this.name = this.route.snapshot.paramMap.get('id');

    // NOTE: Do not use firstValueFrom if user is not signed in, it will silently fail.
    // Using the pipe, catchError and return of(null) with firstValueFrom will not work either.
    // This will handle both signed in and un-assigned users.
    this.userService.doceboUser$.subscribe({
      next: (results) => {
        this.currentUser = results;
      },
      error: (err) => {
        console.log('undefined user', err);
      },
      complete: () => {},
    });
    // Reroute to 404 page if the path does not matches
    this.handleSubscriptionPathRoute();
    // Show or Hide components based on subscription
    this.showSubscriptionComponents();
    // Get selected subscription slug from route snapshot
    this.getSubscriptions();
    // Set title and meta data for selected subscription
    this.setTitleAndMetaData();
    // Set selected testimonials by selected subscription slug
    this.getTestimonials();
    this.toggleHeroButton().then((response) => {
      this.showHeroButton = response;
    });
    // Announcement Content for a subscription
    if (this.selectedSubscriptionUIConfiguration?.subscriptionAnnouncementSlug) {
      this.getAnnouncementContent();
    }
    console.log('SUB DETAIL: ', this.name, this.showContactSales);
  }

  /**
   * Reroute to 404 page if the path does not matches the incoming subscription slug (id)
   */
  handleSubscriptionPathRoute(): void {
    this.name = this.route.snapshot.paramMap.get('id');
    if (
      this.name &&
      this.name !== SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION &&
      this.name !== SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION &&
      this.name !== SubscriptionType.PURECONNECT_SUBSCRIPTION &&
      this.name !== SubscriptionType.AGENT_PLATFORM_SUBSCRIPTION &&
      this.name !== SubscriptionType.AI_ESSENTIAL_SUBSCRIPTION &&
      // NOTE: Pointillist goes end of life 7/31/2026. Removed from whitelist ahead of
      // the 8/3/2026 removal deadline, so this route now 404s.
      this.name !== SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION &&
      this.name !== SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION
    ) {
      this.router.navigateByUrl('pagenotfound');
    }
  }

  /**
   * Show or hide Subscription components based on subscription UI configuraiton by given
   * subscription type
   */
  showSubscriptionComponents(): void {
    this.name = this.route.snapshot.paramMap.get('id');
    this.selectedSubscriptionUIConfiguration =
      this.subscriptionUIItemFactory.getSubscriptionUIConfigurationItem(this.name);
    if (!this.selectedSubscriptionUIConfiguration) {
      console.error(`Subscription configurations cannot be found for ${this.name}`);
    }
  }

  /**
   * On Destroy, unsubscribe subscriptions to prevent memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  /**
   * Open Request a Quote Modal (eloqua), if the user clicks the button on the
   * hero banner
   * @param isClicked - handle event emited from the hero banner
   */
  openModal(isClicked: boolean): void {
    if (isClicked) {
      if (document.getElementById('requestQuote')) {
        document.getElementById('requestQuote').style.display = 'block';

        // NOTE: Fix the background body to be fixed to prevent background scrolling when modal is open
        lockBackgroundScrolling();
      }
    }
  }

  /**
   * Scrolls Into view by given fragment element ID
   * @param fragment element ID to scroll into view
   */
  scrollIntoView(fragment: string): void {
    if (fragment) {
      scrollToPosition(-160, fragment);
    }
  }

  /**
   * Close the Cancel button (eloqua), if the user clicks the button on the Request a Quote form
   * @param isClicked handle event emited from eloqua form
   */
  closeModal(isClicked: boolean): void {
    if (isClicked) {
      if (document.getElementById('requestQuote')) {
        document.getElementById('requestQuote').style.display = 'none';

        // NOTE: Unlock or reset scrolling when closing the modal pop-up
        unlockBackgroundScrolling();
      }
    }
  }

  /**
   * Reset the Form again after user has to click the "x" to close this modal
   * @param isClicked handle event emited from modal
   */
  resetForm(isClicked: boolean): void {
    if (isClicked) {
      if (document.getElementById('eloqSuccessQuoteModal')) {
        loadEloquaForm(EloquaFormType.REQUEST_A_QUOTE);
      }
    }
  }

  /**
   * Subscribe and return testimonials based on subscription
   */
  getTestimonials(): void {
    this.isLoading = true;
    this.subs.push(
      this.wpTestimonialService
        .getTestimonialsByView(TestimonialViewTypes.SUBSCRIPTION_DETAIL)
        .subscribe({
          next: (testimonials: TestimonialInterface[]) => {
            this.isLoading = false;
            if (testimonials) {
              testimonials.forEach((item: TestimonialInterface) => {
                if (item) {
                  const testimonial = this.testimonialAdapter.filter(
                    item,
                    TestimonialViewTypes.SUBSCRIPTION_DETAIL
                  );
                  if (testimonial) {
                    this.testimonials.push(testimonial);
                  }
                  this.setSelectedSubscriptionTestimonial();
                  this.cdRef.markForCheck();
                }
              });
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.log(error);
          },
          complete: () => {
            // Force is loading to stop and complete in case of any unhandled errors
            this.isLoading = false;
            console.log('Subscription Testimonial content completed');
          },
        })
    );
  }

  /**
   * Subscribe and return list of subscriptions
   */
  getSubscriptions(): void {
    // Default Wordpress CMS
    this.isLoading = true;
    // subscription by slug name and return only 1 result.
    let wordpressGeneratedSlug = this.name;
    // NOTE: This is Wordpress slugifying a slug by the name
    // TODO: Need to fix WordPress to read defined slug not generate one from subscription name
    if (this.name === SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION) {
      wordpressGeneratedSlug = SubscriptionType.GENESYS_CLOUD_CX_SUBSCRIPTION;
    } else if (this.name === SubscriptionType.AGENT_PLATFORM_SUBSCRIPTION) {
      wordpressGeneratedSlug = SubscriptionType.AGENT_PLATFORM_AP_SUBSCRIPTION;
    } else if (this.name === SubscriptionType.AI_ESSENTIAL_SUBSCRIPTION) {
      // Use AI slug directly instead of mapping back to BeyondCX
      wordpressGeneratedSlug = 'ai-essentials';
    }
    this.subs.push(
      this.wpSubscriptionService.getSubscriptionBySlug(wordpressGeneratedSlug).subscribe({
        next: (subscriptionItems) => {
          this.isLoading = false;
          subscriptionItems.forEach((item) => {
            if (item) {
              const subscriptionItem = this.subsAdapter.adapt(item);
              this.selectedSubscription = subscriptionItem;
              this.cdRef.markForCheck();
            }
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          // Force is loading to stop and complete in case of any unhandled errors
          this.isLoading = false;
          console.log('Subscriptions content completed');
        },
      })
    );
  }
  /**
   * TODO: Refactor to Subscription Util
   * Return query string to be passed to the search route to filter the catalog
   * by subscription product
   * product = search or product to search
   * @returns search query string based on subscription
   */
  getSearchQuery(): any {
    this.name = this.route.snapshot.paramMap.get('id');
    switch (this.name) {
      case SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION:
        return { product: Product.GENESYS_ENGAGE };
      case SubscriptionType.PURECONNECT_SUBSCRIPTION:
        return { product: Product.PURE_CONNECT };
      case SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION:
        return { product: Product.GENESYS_CLOUD };
      case SubscriptionType.POINTILLIST_SUBSCRIPTION:
        return { product: Product.POINTILLIST };
      default:
        return '';
    }
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Set page title and metadata tags
   */
  setTitleAndMetaData(): void {
    if (this.selectedSubscription) {
      this.titleService.setTitle(`${this.selectedSubscription.name} | Genesys Education`);
      this.metaService.updateTag({
        name: 'description',
        content: this.selectedSubscription.description,
      });
    }
  }

  /**
   * Set selected subscription testimonials based on subscription selected
   */
  setSelectedSubscriptionTestimonial(): void {
    this.name = this.route.snapshot.paramMap.get('id');
    switch (this.name) {
      case SubscriptionType.GENESYS_ENGAGE_SUBSCRIPTION:
        this.selectedSubscriptionTestimonial[0] = this.testimonials[2];
        break;
      case SubscriptionType.PURECONNECT_SUBSCRIPTION:
        this.selectedSubscriptionTestimonial[0] = this.testimonials[1];
        break;
      case SubscriptionType.GENESYS_CLOUD_SUBSCRIPTION:
      case SubscriptionType.GENESYS_CLOUD_PREMIER_PARTNER_SUBSCRIPTION:
      case SubscriptionType.GENESYS_CLOUD_TRIAL_SUBSCRIPTION:
        this.selectedSubscriptionTestimonial[0] = this.testimonials[0];
        break;
      default:
        break;
    }
  }

  /**
   * Get the Certification Announcement content.
   */
  getAnnouncementContent(): void {
    this.isLoading = true;
    this.subs.push(
      this.announcementService
        .getAnnouncementContentBySlug(
          this.selectedSubscriptionUIConfiguration.subscriptionAnnouncementSlug
        )
        .subscribe({
          next: (response: StaticPageInterface[]) => {
            if (response && response.length > 0) {
              this.announcementContent = this.staticPageAdapter.adapt(response[0]);
              this.isLoading = false;
              this.cdRef.markForCheck();
            }
          },
          error: (error) => {
            this.isLoading = false;
            console.log(error);
          },
          complete: () => {
            // Force is loading to stop and complete in case of any unhandled errors
            this.isLoading = false;
            console.log('Subscription Announcement content completed');
          },
        })
    );
  }

  /**
   * Compares the selected subscription value with the user's active
   * subscriptions and set the display of the Hero Button
   * @returns boolean value to set the display of Hero Button
   */
  async toggleHeroButton(): Promise<boolean> {
    if (
      this.name === SubscriptionType.PURECONNECT_SUBSCRIPTION ||
      this.name === SubscriptionType.AI_ESSENTIAL_SUBSCRIPTION
    )
      return false;

    let subscriptionArray = [];
    if (this.currentUser) {
      await this.subscriptionService.getActiveSubscriptions(this.currentUser).then((response) => {
        response.forEach((subscription) => {
          const subscriptionSlug = getSubscriptionSlugByProductShortName(subscription);
          subscriptionArray.push(subscriptionSlug);
        });
      });
    }
    return subscriptionArray.includes(this.name) ? false : true;
  }

  /**
   * Subscription Type enums based on subscription types
   */
  public get subscriptionType(): typeof SubscriptionType {
    return SubscriptionType;
  }

  /**
   *  CardTypeCallToActions enums based on card types
   */
  public get cardTypeCallToActions(): typeof CardTypeCallToActions {
    return CardTypeCallToActions;
  }

  /**
   *  RouteTypes enums based on route types
   */
  public get routeTypes(): typeof RouteTypes {
    return RouteTypes;
  }

  /**
   *  ProductShortNames enums based on product short names
   */
  public get productShortName(): typeof ProductShortName {
    return ProductShortName;
  }
}
